"use client";

import { useRouter } from "next/navigation";
import { useState, useTransition, useMemo } from "react";
import { ChevronDown } from "lucide-react";

import SearchContainer from "@/components/search/container";
import Select from "@/components/search/select";
import { adTypeOption } from "@/constants/adType";
import { propertyTypeOption } from "@/constants/propertyType";
import { SearchInput } from "@/components/search/input";
import LayoutWrapper from "@/components/layout/wrapper";
import AreaSelectionModal from "@/components/agency-area-selection";
import { useAreaSelection } from "@/hook/area-selection";

export default function AdvertisementsFilter({
  landing = false,
  customer = false,
}: Readonly<{
  landing?: boolean;
  customer?: boolean;
}>) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const [adType, setAdType] = useState<string | undefined>(undefined);
  const [propertyType, setPropertyType] = useState<number | undefined>(
    undefined,
  );
  const [title, setTitle] = useState("");

  const {
    selectedpath,
    areaModalOpen,
    handleAreaModalOpen,
    handleAreaModalClose,
    chipData,
    setChipData,
    provinceData,
    pathData,
    setPathData,
    areaDetails,
    stepCounter,
    setStepCounter,
    provinceLoading,
    selectedRadioId,
    setSelectedRadioId,
    detailsLoading,
    checkRadioRequestToSendarea_id,
    selectedAreaText,
    selectedAreaId,
    handleBack,
    handleCoverageAreaId,
    handleRadioOnClick,
  } = useAreaSelection({ clearCoverageStorageOnMount: true });

  const isSearchEnabled = useMemo(
    () => !!adType && !!propertyType,
    [adType, propertyType],
  );

  const handleSearch = () => {
    if (!isSearchEnabled) return;

    const params = new URLSearchParams();
    params.set("ad_type", adType!);
    params.set("property_type", propertyType!.toString());
    if (selectedAreaId) params.set("area", selectedAreaId);
    if (title.trim()) params.set("title", title.trim());
    params.set("page", "1");

    startTransition(() => {
      router.push(
        `/advertisements-consultants/advertisements-filter/list?${params.toString()}`,
      );
    });
  };

  return (
    <LayoutWrapper
      parentClassName={landing ? "min-h-auto" : "min-h-screen"}
      className={landing ? "min-h-auto" : "min-h-screen"}
    >
      <div className={`flex flex-col items-center gap-10 md:gap-16 lg:gap-20 ${!landing && "px-3"}  md:px-4 pb-12 md:pb-16 w-full mt-5 md:my-10`}>
        {!landing && (
          <strong className="text-sec-title text-3xl md:text-[38px] font-bold text-center max-w-4xl">
            فقط چند کلیک تا پیدا کردن بهترین ملک فاصله داری
          </strong>
        )}
        <SearchContainer
          onSearch={handleSearch}
          isSearchEnabled={isSearchEnabled}
          isLoading={isPending}
        >
          <SearchInput
            placeholder="عنوان آگهی را وارد کنید"
            value={title}
            onChange={setTitle}
          />

          <Select
            label="نوع معامله"
            options={adTypeOption}
            value={adType}
            onChange={(option) => setAdType(option.value as string)}
          />
          <Select
            label="نوع آگهی"
            options={propertyTypeOption}
            value={propertyType}
            onChange={(option) => setPropertyType(option.value as number)}
          />
          {/* Area selection field - clickable like search input */}
          {!customer && (
            <button
              type="button"
              aria-label="منطقه تحت پوشش"
              className={`
    relative flex w-full min-w-0 items-center justify-start gap-1.5 py-1 text-right
    bg-transparent border-0
    cursor-pointer group transition-all duration-200
  `}
              onClick={handleAreaModalOpen}
            >
              <span
                className={`
      text-sm font-medium truncate
      ${selectedpath?.length > 0 ? "text-gray-900" : "text-inp text-base font-normal"}
    `}
              >
                {selectedpath?.length > 0
                  ? selectedAreaText
                  : "منطقه تحت پوشش"}
              </span>

              <ChevronDown
                size={16}
                className="text-gray-400 group-hover:text-indigo-500 transition-colors shrink-0"
              />
            </button>
          )}
        </SearchContainer>
        {!landing && (
          <div className="mt-8 w-full">
            <img
              src="/assets/svg/advertisement-filter.svg"
              alt=""
              className="mx-auto h-auto w-full max-w-xs overflow-hidden sm:max-w-md md:max-w-xl"
            />
          </div>
        )}
      </div>

      <AreaSelectionModal
        open={areaModalOpen}
        onClose={handleAreaModalClose}
        agencyArea={provinceData}
        areaDetails={areaDetails}
        agencyAreaLoading={provinceLoading}
        areaDetailsLoading={detailsLoading}
        handleRadioOnClick={handleRadioOnClick}
        chipData={chipData}
        setChipData={setChipData}
        checkRadioRequestToSendarea_id={checkRadioRequestToSendarea_id}
        stepCounter={stepCounter}
        setStepCounter={setStepCounter}
        handleCoverageAreaId={handleCoverageAreaId}
        selectedRadioId={selectedRadioId}
        setSelectedRadioId={setSelectedRadioId}
        handleBack={handleBack}
        typeModal="outside"
        pathData={pathData}
        setPathData={setPathData}
        agencyAreaAdd={true}
      />
    </LayoutWrapper>
  );
}
